import React, { useState, useEffect } from 'react';
import { Modal, Button, Form, Alert, Spinner, Table, Badge } from 'react-bootstrap';
import { FaExclamationTriangle, FaCheckCircle, FaUsers } from 'react-icons/fa';

const MarkClassAsFakeModal = ({ show, onHide, schoolid, getClassesBySchoolId, updateTrialStatusBatch }) => {
  const [classes, setClasses] = useState([]);
  const [selectedClasses, setSelectedClasses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [markingType, setMarkingType] = useState('fake'); // 'fake' or 'real'
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    if (show && schoolid) {
      fetchClasses();
    }
  }, [show, schoolid, markingType]); // Added markingType dependency

  const fetchClasses = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await getClassesBySchoolId(schoolid);
      
      if (response && Array.isArray(response)) {
        // Filter classes based on markingType
        let filteredClasses;
        if (markingType === 'fake') {
          // Show real classes (to mark as trial)
          filteredClasses = response.filter(cls => !cls.trial_class);
        } else {
          // Show trial classes (to mark as real)
          filteredClasses = response.filter(cls => cls.trial_class);
        }
        setClasses(filteredClasses);
      } else if (response && response.data && Array.isArray(response.data)) {
        // API returns classes wrapped in data property
        let filteredClasses;
        if (markingType === 'fake') {
          // Show real classes (to mark as trial)
          filteredClasses = response.data.filter(cls => !cls.trial_class);
        } else {
          // Show trial classes (to mark as real)
          filteredClasses = response.data.filter(cls => cls.trial_class);
        }
        setClasses(filteredClasses);
      } else {
        setClasses([]);
      }
    } catch (err) {
      setError('Failed to fetch classes. Please try again.');
      console.error('Error fetching classes:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleClassSelection = (classId) => {
    setSelectedClasses(prev => {
      if (prev.includes(classId)) {
        return prev.filter(id => id !== classId);
      } else {
        return [...prev, classId];
      }
    });
  };

  const handleSelectAll = () => {
    if (selectedClasses.length === filteredClasses.length) {
      setSelectedClasses([]);
    } else {
      setSelectedClasses(filteredClasses.map(cls => cls.id));
    }
  };

  const handleSubmit = async () => {
    if (selectedClasses.length === 0) {
      setError('Please select at least one class.');
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      // Prepare data for API call
      const classIds = selectedClasses; // Array of class IDs
      const isTrialClass = markingType === 'fake'; // true for trial, false for real
      
      console.log('Calling API with:');
      console.log('Class IDs:', classIds);
      console.log('isTrialClass:', isTrialClass);
      
      // Call the API
      const result = await updateTrialStatusBatch(classIds, isTrialClass);
      
      console.log('API Response:', result);
      
      // Handle the response
      if (result.failed && result.failed.length > 0) {
        // Some classes failed to update
        const failedCount = result.failed.length;
        const successCount = result.updated || (classIds.length - failedCount);
        
        if (successCount > 0) {
          const actionText = markingType === 'fake' ? 'marked as fake classes' : 'marked as real classes';
          setSuccess(`${successCount} class(es) successfully ${actionText}.`);
        }
        
        const failedErrors = result.failed.map(f => f.error).join(', ');
        setError(`${failedCount} class(es) failed to update: ${failedErrors}`);
      } else {
        // All classes updated successfully
        const actionText = markingType === 'fake' ? 'marked as fake classes' : 'marked as real classes';
        setSuccess(`${result.updated || classIds.length} class(es) successfully ${actionText}.`);
      }
      
      setSelectedClasses([]);
      
      // Refresh the classes list
      await fetchClasses();
      
    } catch (err) {
      setError(`Failed to update classes: ${err.message}`);
      console.error('Error updating classes:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setSelectedClasses([]);
    setError('');
    setSuccess('');
    setMarkingType('fake');
    setSearchTerm('');
    onHide();
  };

  // Filter classes based on search term
  const filteredClasses = classes.filter(cls => {
    const className = (cls.name || '').toLowerCase();
    const gradeLevel = (cls.grade_level || '').toLowerCase();
    const search = searchTerm.toLowerCase();
    
    return className.includes(search) || gradeLevel.includes(search);
  });

  const getClassStatusBadge = (isTrialClass) => {
    if (isTrialClass) {
      return <Badge bg="warning" text="dark">Fake Class</Badge>;
    }
    return <Badge bg="success">Real Class</Badge>;
  };

  // Calculate totals for selected classes
  const selectedClassesData = filteredClasses.filter(cls => selectedClasses.includes(cls.id));
  const totalSelectedStudents = selectedClassesData.reduce((sum, cls) => sum + (cls.studentNumber || 0), 0);

  return (
    <Modal show={show} onHide={handleClose} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>
          <FaExclamationTriangle className="me-2 text-warning" />
          Mark Classes as Fake/Real
        </Modal.Title>
      </Modal.Header>
      
      <Modal.Body>
        {error && (
          <Alert variant="danger" dismissible onClose={() => setError('')}>
            {error}
          </Alert>
        )}
        
        {success && (
          <Alert variant="success" dismissible onClose={() => setSuccess('')}>
            <FaCheckCircle className="me-2" />
            {success}
          </Alert>
        )}

        <div className="mb-3">
          <Form.Group>
            <Form.Label className="fw-bold">Action Type:</Form.Label>
            <Form.Select 
              value={markingType} 
              onChange={(e) => setMarkingType(e.target.value)}
              disabled={loading}
            >
              <option value="fake">Mark as Fake Class</option>
              <option value="real">Mark as Real Class</option>
            </Form.Select>
          </Form.Group>
        </div>

        {/* Search Box */}
        <div className="mb-3">
          <Form.Group>
            <Form.Label className="fw-bold">Search Classes:</Form.Label>
            <Form.Control
              type="text"
              placeholder="Search by class name or grade level..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              disabled={loading}
            />
          </Form.Group>
        </div>

        <div className="mb-3">
          <div className="d-flex justify-content-between align-items-center">
            <h6 className="mb-0">
              Select Classes ({selectedClasses.length} selected{searchTerm && ` | ${filteredClasses.length} shown`})
              {selectedClasses.length > 0 && (
                <span className="text-muted ms-2">
                  <FaUsers className="me-1" />
                  {totalSelectedStudents} students
                </span>
              )}
            </h6>
            <Button 
              variant="outline-secondary" 
              size="sm"
              onClick={handleSelectAll}
              disabled={loading || filteredClasses.length === 0}
            >
              {selectedClasses.length === filteredClasses.length ? 'Deselect All' : 'Select All'}
            </Button>
          </div>
        </div>

        {loading && classes.length === 0 ? (
          <div className="text-center py-4">
            <Spinner animation="border" role="status">
              <span className="visually-hidden">Loading classes...</span>
            </Spinner>
            <p className="mt-2 text-muted">Loading classes...</p>
          </div>
        ) : filteredClasses.length === 0 && classes.length > 0 ? (
          <Alert variant="info">
            No classes found matching "{searchTerm}".
          </Alert>
        ) : classes.length === 0 ? (
          <Alert variant="info">
            No classes found for this school.
          </Alert>
        ) : (
          <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
            <Table striped bordered hover size="sm">
              <thead className="table-dark">
                <tr>
                  <th width="50">
                    <Form.Check 
                      type="checkbox"
                      checked={selectedClasses.length === filteredClasses.length && filteredClasses.length > 0}
                      onChange={handleSelectAll}
                      disabled={loading}
                    />
                  </th>
                  <th>Class Name</th>
                  <th>Grade</th>
                  <th width="120">Students</th>
                  <th width="120">Status</th>
                </tr>
              </thead>
              <tbody>
                {filteredClasses.map((cls) => (
                  <tr key={cls.id}>
                    <td>
                      <Form.Check 
                        type="checkbox"
                        checked={selectedClasses.includes(cls.id)}
                        onChange={() => handleClassSelection(cls.id)}
                        disabled={loading}
                      />
                    </td>
                    <td className="fw-bold">{cls.name || `Class ${cls.id}`}</td>
                    <td>{cls.grade_level || 'N/A'}</td>
                    <td>
                      <div className="d-flex align-items-center">
                        <FaUsers className="me-1 text-muted" size={12} />
                        <span className="fw-bold text-primary">
                          {cls.studentNumber || 0}
                        </span>
                      </div>
                    </td>
                    <td>{getClassStatusBadge(cls.trial_class)}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        )}

        {markingType === 'fake' && (
          <Alert variant="warning" className="mt-3">
            <FaExclamationTriangle className="me-2" />
            <strong>Warning:</strong> Classes marked as fake will be treated differently in the system. 
            This action can be reversed by marking them as real classes.
          </Alert>
        )}
      </Modal.Body>
      
      <Modal.Footer>
        <Button 
          variant="secondary" 
          onClick={handleClose}
          disabled={loading}
        >
          Cancel
        </Button>
        <Button 
          variant={markingType === 'fake' ? 'warning' : 'success'}
          onClick={handleSubmit}
          disabled={loading || selectedClasses.length === 0}
        >
          {loading ? (
            <>
              <Spinner animation="border" size="sm" className="me-2" />
              Processing...
            </>
          ) : (
            <>
              {markingType === 'fake' ? 'Mark as Fake' : 'Mark as Real'}
              {selectedClasses.length > 0 && ` (${selectedClasses.length})`}
            </>
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default MarkClassAsFakeModal;